import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../api";

// ─────────────────────────────────────────────
// Shared UI primitives
// ─────────────────────────────────────────────
const S = {
  page:    { display:"flex", minHeight:"100vh", background:"#0f0f0f", fontFamily:"var(--font)" },
  sidebar: { width:230, background:"#0B0B0B", borderRight:"1px solid rgba(255,255,255,0.07)", display:"flex", flexDirection:"column", padding:"1.5rem 1rem", flexShrink:0, position:"sticky", top:0, height:"100vh", overflowY:"auto" },
  main:    { flex:1, overflowY:"auto", padding:"2rem" },
  card:    { background:"#1a1a1a", border:"1px solid rgba(255,255,255,0.08)", borderRadius:14, padding:"1.5rem", marginBottom:"1rem" },
  row:     { display:"flex", alignItems:"center", gap:"1rem", background:"#1a1a1a", border:"1px solid rgba(255,255,255,0.07)", borderRadius:12, padding:"1rem 1.25rem", marginBottom:"0.6rem" },
  label:   { display:"block", fontSize:"0.72rem", color:"rgba(255,255,255,0.4)", marginBottom:"0.35rem", letterSpacing:"0.06em", textTransform:"uppercase" },
  input:   { background:"rgba(255,255,255,0.07)", border:"1.5px solid rgba(255,255,255,0.12)", borderRadius:8, padding:"0.75rem 1rem", color:"#fff", fontSize:"0.9rem", width:"100%", fontFamily:"var(--font-body)", outline:"none" },
  grid2:   { display:"grid", gridTemplateColumns:"1fr 1fr", gap:"1rem" },
  error:   { color:"#f09595", fontSize:"0.82rem", marginTop:"0.5rem" },
  badge:   (color) => ({ background:`${color}22`, color, borderRadius:100, padding:"0.2rem 0.7rem", fontSize:"0.65rem", fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase" }),
};

function Input({ label, span, children, ...props }) {
  return (
    <div style={{ gridColumn: span === 2 ? "1/-1" : "auto" }}>
      {label && <label style={S.label}>{label}</label>}
      {children || <input style={S.input} {...props} />}
    </div>
  );
}

function Textarea({ label, span, ...props }) {
  return (
    <div style={{ gridColumn: span === 2 ? "1/-1" : "auto" }}>
      {label && <label style={S.label}>{label}</label>}
      <textarea style={{ ...S.input, resize:"vertical" }} {...props} />
    </div>
  );
}

function Select({ label, span, options, ...props }) {
  return (
    <div style={{ gridColumn: span === 2 ? "1/-1" : "auto" }}>
      {label && <label style={S.label}>{label}</label>}
      <select style={{ ...S.input, cursor:"pointer" }} {...props}>
        {options.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <label style={{ display:"flex", alignItems:"center", gap:"0.6rem", cursor:"pointer", fontSize:"0.88rem", color:"rgba(255,255,255,0.7)" }}>
      <div onClick={onChange} style={{ width:42, height:24, borderRadius:12, background: checked ? "#8A1613" : "rgba(255,255,255,0.1)", position:"relative", transition:"background 0.2s", cursor:"pointer", flexShrink:0 }}>
        <div style={{ position:"absolute", top:3, left: checked ? 20 : 3, width:18, height:18, borderRadius:"50%", background:"#fff", transition:"left 0.2s" }} />
      </div>
      {label}
    </label>
  );
}

function FileInput({ label, span, accept, note }) {
  return (
    <div style={{ gridColumn: span === 2 ? "1/-1" : "auto" }}>
      {label && <label style={S.label}>{label}</label>}
      <input type="file" accept={accept||"image/*"} style={{ ...S.input, cursor:"pointer", padding:"0.5rem 1rem" }} />
      {note && <p style={{ fontSize:"0.72rem", color:"rgba(255,255,255,0.3)", marginTop:"0.3rem" }}>{note}</p>}
    </div>
  );
}

// Password strength validator — returns null if ok, else an error string
function validatePassword(pw) {
  if (!pw || pw.length < 8)    return "Password must be at least 8 characters";
  if (!/[A-Z]/.test(pw))       return "Must include at least one uppercase letter";
  if (!/[a-z]/.test(pw))       return "Must include at least one lowercase letter";
  if (!/[0-9]/.test(pw))       return "Must include at least one number";
  return null;
}

// Password requirements indicator
function PwRequirements({ pw }) {
  if (!pw) return null;
  const checks = [
    { ok: pw.length >= 8,      label: "8+ characters" },
    { ok: /[A-Z]/.test(pw),    label: "Uppercase" },
    { ok: /[a-z]/.test(pw),    label: "Lowercase" },
    { ok: /[0-9]/.test(pw),    label: "Number" },
  ];
  return (
    <div style={{ display:"flex", gap:"0.5rem", flexWrap:"wrap", marginTop:"0.35rem" }}>
      {checks.map(c => (
        <span key={c.label} style={{ fontSize:"0.7rem", padding:"0.15rem 0.55rem", borderRadius:100, background: c.ok ? "rgba(149,187,131,0.15)" : "rgba(255,255,255,0.06)", color: c.ok ? "#95BB83" : "rgba(255,255,255,0.35)", fontWeight:600 }}>
          {c.ok ? "✓" : "·"} {c.label}
        </span>
      ))}
    </div>
  );
}

// Password input with show/hide toggle
function PwInput({ label, value, onChange, placeholder, showReqs = false }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      {label && <label style={S.label}>{label}</label>}
      <div style={{ position:"relative" }}>
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          style={{ ...S.input, paddingRight:"3rem", boxSizing:"border-box" }}
        />
        <button
          type="button"
          onClick={() => setShow(v => !v)}
          style={{ position:"absolute", right:"0.75rem", top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", fontSize:"1rem", color:"rgba(255,255,255,0.35)", lineHeight:1, padding:0 }}
          title={show ? "Hide" : "Show"}
        >{show ? "🙈" : "👁"}</button>
      </div>
      {showReqs && <PwRequirements pw={value} />}
    </div>
  );
}

function BtnPrimary({ children, onClick, disabled, style={} }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ background:"#8A1613", color:"#fff", border:"none", borderRadius:100, padding:"0.75rem 1.75rem", fontSize:"0.88rem", fontWeight:700, cursor:"pointer", fontFamily:"var(--font)", opacity:disabled?0.6:1, transition:"background 0.2s", ...style }}
      onMouseEnter={e=>!disabled&&(e.target.style.background="#b01e1a")}
      onMouseLeave={e=>!disabled&&(e.target.style.background="#8A1613")}>
      {children}
    </button>
  );
}

function BtnGhost({ children, onClick, style={} }) {
  return (
    <button onClick={onClick} style={{ background:"transparent", color:"rgba(255,255,255,0.6)", border:"1px solid rgba(255,255,255,0.15)", borderRadius:100, padding:"0.7rem 1.5rem", fontSize:"0.85rem", cursor:"pointer", fontFamily:"var(--font)", ...style }}>
      {children}
    </button>
  );
}

function BtnDanger({ children, onClick }) {
  return (
    <button onClick={onClick} style={{ background:"rgba(200,50,50,0.15)", color:"#f09595", border:"none", borderRadius:8, padding:"0.4rem 0.9rem", fontSize:"0.78rem", cursor:"pointer", fontFamily:"var(--font)", fontWeight:600 }}>
      {children}
    </button>
  );
}

function BtnEdit({ onClick }) {
  return (
    <button onClick={onClick} style={{ background:"rgba(255,255,255,0.07)", color:"rgba(255,255,255,0.8)", border:"none", borderRadius:8, padding:"0.4rem 0.9rem", fontSize:"0.78rem", cursor:"pointer", fontFamily:"var(--font)", fontWeight:600 }}>
      Edit
    </button>
  );
}

function SectionHeader({ title, onAdd, addLabel }) {
  return (
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1.5rem" }}>
      <h2 style={{ fontFamily:"var(--font)", fontWeight:800, fontSize:"1.4rem", color:"#fff", margin:0 }}>{title}</h2>
      {onAdd && <BtnPrimary onClick={onAdd}>{addLabel || "+ Add"}</BtnPrimary>}
    </div>
  );
}

function FormWrap({ title, onClose, children }) {
  return (
    <div style={{ ...S.card, border:"1px solid rgba(138,22,19,0.3)" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1.5rem" }}>
        <h3 style={{ fontFamily:"var(--font)", fontWeight:800, fontSize:"1.1rem", color:"#fff", margin:0 }}>{title}</h3>
        <button onClick={onClose} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.4)", fontSize:"1.2rem", cursor:"pointer" }}>✕</button>
      </div>
      {children}
    </div>
  );
}

function FormActions({ onSave, onCancel, saving, saveLabel }) {
  return (
    <div style={{ display:"flex", gap:"0.75rem", marginTop:"1.5rem", gridColumn:"1/-1" }}>
      <BtnPrimary onClick={onSave} disabled={saving}>{saving ? "Saving…" : (saveLabel || "Save")}</BtnPrimary>
      <BtnGhost onClick={onCancel}>Cancel</BtnGhost>
    </div>
  );
}

// ─────────────────────────────────────────────
// Generic hook for CRUD sections
// ─────────────────────────────────────────────
function useSection(loadFn) {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm,setShowForm]= useState(false);
  const [editing, setEditing] = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState("");

  const load = () => { setLoading(true); loadFn().then(setItems).catch(()=>{}).finally(()=>setLoading(false)); };
  useEffect(load, []);

  const openAdd  = ()  => { setEditing(null); setErr(""); setShowForm(true); };
  const openEdit = (i) => { setEditing(i);    setErr(""); setShowForm(true); };
  const close    = ()  => { setShowForm(false); setEditing(null); setErr(""); };

  const save = async (submitFn) => {
    setSaving(true); setErr("");
    try {
      const saved = await submitFn();
      setItems(prev => editing ? prev.map(x => x.id===saved.id ? saved : x) : [saved, ...prev]);
      close();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const remove = async (id, deleteFn) => {
    if (!window.confirm("Delete this item?")) return;
    await deleteFn(id).catch(()=>{});
    setItems(prev => prev.filter(x => x.id !== id));
  };

  return { items, loading, showForm, editing, saving, err, setErr, openAdd, openEdit, close, save, remove, reload: load };
}

// ─────────────────────────────────────────────
// CAMPAIGNS
// ─────────────────────────────────────────────
function CampaignForm({ editing, onSave, onCancel, saving, err }) {
  const empty = { title:"", client:"", category:"", year:new Date().getFullYear(), reach:"", description:"", tag:"", color:"#8A1613", accent:"#95BB83", featured:false, published:true };
  const [f, setF] = useState(editing || empty);
  const [img, setImg] = useState(null);
  const set = (k,v) => setF(p=>({...p,[k]:v}));

  const submit = () => {
    const fd = new FormData();
    Object.entries(f).forEach(([k,v])=>fd.append(k,v));
    if (img) fd.append("image", img);
    return editing ? api.updateCampaign(editing.id, fd) : api.createCampaign(fd);
  };

  return (
    <div style={S.grid2}>
      <Input label="Title *"        value={f.title}       onChange={e=>set("title",e.target.value)}       placeholder="Campaign title" />
      <Input label="Client *"       value={f.client}      onChange={e=>set("client",e.target.value)}      placeholder="Client or organisation" />
      <Input label="Category *"     value={f.category}    onChange={e=>set("category",e.target.value)}    placeholder="e.g. TB, HIV/AIDS, Diabetes" />
      <Input label="Year *"         value={f.year}        onChange={e=>set("year",e.target.value)}        type="number" min="2000" max="2099" />
      <Input label="Reach *"        value={f.reach}       onChange={e=>set("reach",e.target.value)}       placeholder="e.g. 2.4M, 890K" />
      <Input label="Short Tag *"    value={f.tag}         onChange={e=>set("tag",e.target.value.toUpperCase())} placeholder="TB / HIV / DM" maxLength={8} />
      <Textarea label="Description *" span={2} value={f.description} onChange={e=>set("description",e.target.value)} rows={4} placeholder="Campaign description..." />
      <div>
        <label style={S.label}>Primary Colour</label>
        <input type="color" value={f.color} onChange={e=>set("color",e.target.value)} style={{ ...S.input, height:46, padding:"0.3rem 0.5rem", cursor:"pointer" }} />
      </div>
      <div>
        <label style={S.label}>Accent Colour</label>
        <input type="color" value={f.accent} onChange={e=>set("accent",e.target.value)} style={{ ...S.input, height:46, padding:"0.3rem 0.5rem", cursor:"pointer" }} />
      </div>
      <div style={{ gridColumn:"1/-1" }}>
        <label style={S.label}>Campaign Image (optional)</label>
        <input type="file" accept="image/*" onChange={e=>setImg(e.target.files[0])} style={{ ...S.input, padding:"0.5rem 1rem", cursor:"pointer" }} />
        {editing?.image_url && !img && <img src={editing.image_url} alt="current" style={{ width:80, height:60, objectFit:"cover", borderRadius:8, marginTop:"0.5rem" }} />}
      </div>
      <div style={{ gridColumn:"1/-1", display:"flex", gap:"2rem" }}>
        <Toggle label="Featured on homepage" checked={!!f.featured} onChange={()=>set("featured",!f.featured)} />
        <Toggle label="Published (visible)"  checked={!!f.published} onChange={()=>set("published",!f.published)} />
      </div>
      {err && <p style={{ ...S.error, gridColumn:"1/-1" }}>{err}</p>}
      <FormActions onSave={()=>onSave(submit)} onCancel={onCancel} saving={saving} />
    </div>
  );
}

function CampaignsSection() {
  const { items, loading, showForm, editing, saving, err, openAdd, openEdit, close, save, remove } = useSection(()=>api.adminAll("campaigns"));
  return (
    <>
      <SectionHeader title="Campaigns" onAdd={openAdd} addLabel="+ Add Campaign" />
      {showForm && (
        <FormWrap title={editing ? "Edit Campaign" : "New Campaign"} onClose={close}>
          <CampaignForm editing={editing} onSave={save} onCancel={close} saving={saving} err={err} />
        </FormWrap>
      )}
      {loading ? <p style={{ color:"rgba(255,255,255,0.4)" }}>Loading…</p> : items.map(c => (
        <div key={c.id} style={S.row}>
          <div style={{ width:44, height:44, borderRadius:10, background:`linear-gradient(135deg,${c.color},${c.accent})`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.7rem", fontWeight:800, color:"rgba(255,255,255,0.9)", flexShrink:0 }}>{c.tag}</div>
          <div style={{ flex:1 }}>
            <p style={{ fontWeight:600, fontSize:"0.92rem", color:"#fff", margin:0 }}>{c.title}</p>
            <p style={{ fontSize:"0.75rem", color:"rgba(255,255,255,0.35)", margin:0 }}>{c.client} · {c.category} · {c.year} · {c.reach}</p>
          </div>
          <div style={{ display:"flex", gap:"0.5rem", alignItems:"center" }}>
            {c.featured ? <span style={S.badge("#95BB83")}>Featured</span> : null}
            {!c.published ? <span style={S.badge("rgba(255,255,255,0.4)")}>Draft</span> : null}
            <BtnEdit onClick={()=>openEdit(c)} />
            <BtnDanger onClick={()=>remove(c.id, api.deleteCampaign)}>Delete</BtnDanger>
          </div>
        </div>
      ))}
    </>
  );
}

// ─────────────────────────────────────────────
// EPISODES
// ─────────────────────────────────────────────
function EpisodeForm({ editing, onSave, onCancel, saving, err }) {
  const empty = {
    title:"", description:"", guest_name:"", guest_title:"", topic:"",
    season:1, episode_number:"", duration_mins:0,
    publish_date: new Date().toISOString().slice(0,10),
    spotify_url:"", apple_url:"", audio_url:"",
    published: true,
    notify_subscribers: false,   // ← NEW
  };
  const [f, setF] = useState(editing || empty);
  const [audio, setAudio] = useState(null);
  const [cover, setCover] = useState(null);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const submit = () => {
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    // Ensure booleans are sent as "1"/"0" not "true"/"false"
    fd.set("published",            f.published            ? "1" : "0");
    fd.set("notify_subscribers",   f.notify_subscribers   ? "1" : "0");
    if (audio) fd.append("audio", audio);
    if (cover) fd.append("cover", cover);
    return editing ? api.updateEpisode(editing.id, fd) : api.createEpisode(fd);
  };

  return (
    <div style={S.grid2}>
      <Input label="Title *" span={2} value={f.title} onChange={e=>set("title",e.target.value)} placeholder="Episode title" />
      <Input label="Guest Name" value={f.guest_name} onChange={e=>set("guest_name",e.target.value)} placeholder="Dr. Jane Smith" />
      <Input label="Guest Title / Role" value={f.guest_title} onChange={e=>set("guest_title",e.target.value)} placeholder="Pulmonologist, Groote Schuur" />
      <Input label="Topic *" value={f.topic} onChange={e=>set("topic",e.target.value)} placeholder="e.g. HIV/AIDS, TB, Mental Health" />
      <Input label="Season" value={f.season} onChange={e=>set("season",e.target.value)} type="number" min="1" />
      <Input label="Episode Number *" value={f.episode_number} onChange={e=>set("episode_number",e.target.value)} type="number" min="1" />
      <Input label="Duration (minutes)" value={f.duration_mins} onChange={e=>set("duration_mins",e.target.value)} type="number" min="0" />
      <Input label="Publish Date *" value={f.publish_date} onChange={e=>set("publish_date",e.target.value)} type="date" />
      <Textarea label="Description *" span={2} value={f.description} onChange={e=>set("description",e.target.value)} rows={4} placeholder="Episode description…" />
      <div>
        <label style={S.label}>Audio File (upload)</label>
        <input type="file" accept="audio/*" onChange={e=>setAudio(e.target.files[0])} style={{ ...S.input, padding:"0.5rem", cursor:"pointer" }} />
      </div>
      <Input label="OR Audio URL (paste link)" value={f.audio_url} onChange={e=>set("audio_url",e.target.value)} placeholder="https://…" />
      <div>
        <label style={S.label}>Cover Image</label>
        <input type="file" accept="image/*" onChange={e=>setCover(e.target.files[0])} style={{ ...S.input, padding:"0.5rem", cursor:"pointer" }} />
      </div>
      <Input label="Spotify Episode URL" value={f.spotify_url} onChange={e=>set("spotify_url",e.target.value)} placeholder="https://open.spotify.com/episode/…" />
      <Input label="Apple Podcasts URL" value={f.apple_url} onChange={e=>set("apple_url",e.target.value)} placeholder="https://podcasts.apple.com/…" />

      {/* Published + Notify toggles */}
      <div style={{ gridColumn:"1/-1", display:"flex", flexDirection:"column", gap:"0.85rem" }}>
        <Toggle label="Published (visible on site)" checked={!!f.published} onChange={()=>set("published",!f.published)} />
        {!editing && (
          <Toggle
            label="📧 Notify newsletter subscribers when published"
            checked={!!f.notify_subscribers}
            onChange={()=>set("notify_subscribers",!f.notify_subscribers)}
          />
        )}
      </div>

      {err && <p style={{ ...S.error, gridColumn:"1/-1" }}>{err}</p>}
      <FormActions onSave={()=>onSave(submit)} onCancel={onCancel} saving={saving} />
    </div>
  );
}

function EpisodesSection() {
  const { items, loading, showForm, editing, saving, err, openAdd, openEdit, close, save, remove } = useSection(()=>api.adminAll("episodes"));
  return (
    <>
      <SectionHeader title="Podcast Episodes" onAdd={openAdd} addLabel="+ Add Episode" />
      {showForm && (
        <FormWrap title={editing ? "Edit Episode" : "New Episode"} onClose={close}>
          <EpisodeForm editing={editing} onSave={save} onCancel={close} saving={saving} err={err} />
        </FormWrap>
      )}
      {loading ? <p style={{ color:"rgba(255,255,255,0.4)" }}>Loading…</p> : items.map(ep => (
        <div key={ep.id} style={S.row}>
          <div style={{ width:44, height:44, borderRadius:10, background:"rgba(138,22,19,0.3)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.2rem", flexShrink:0 }}>🎙</div>
          <div style={{ flex:1 }}>
            <p style={{ fontWeight:600, fontSize:"0.92rem", color:"#fff", margin:0 }}>{ep.title}</p>
            <p style={{ fontSize:"0.75rem", color:"rgba(255,255,255,0.35)", margin:0 }}>S{ep.season}E{ep.episode_number} · {ep.topic} · {ep.duration_mins} min{ep.guest_name ? ` · ${ep.guest_name}` : ""}</p>
          </div>
          <div style={{ display:"flex", gap:"0.5rem" }}>
            {!ep.published && <span style={S.badge("rgba(255,255,255,0.4)")}>Draft</span>}
            <BtnEdit onClick={()=>openEdit(ep)} />
            <BtnDanger onClick={()=>remove(ep.id, api.deleteEpisode)}>Delete</BtnDanger>
          </div>
        </div>
      ))}
    </>
  );
}

// ─────────────────────────────────────────────
// TEAM
// ─────────────────────────────────────────────
function TeamForm({ editing, onSave, onCancel, saving, err }) {
  const empty = { name:"", role:"", bio:"", email:"", linkedin_url:"", sort_order:0, published:true };
  const [f, setF] = useState(editing || empty);
  const [photo, setPhoto] = useState(null);
  const set = (k,v) => setF(p=>({...p,[k]:v}));

  const submit = () => {
    const fd = new FormData();
    Object.entries(f).forEach(([k,v])=>fd.append(k,v));
    if (photo) fd.append("photo", photo);
    return editing ? api.updateTeamMember(editing.id, fd) : api.createTeamMember(fd);
  };

  return (
    <div style={S.grid2}>
      <Input label="Full Name *" value={f.name} onChange={e=>set("name",e.target.value)} placeholder="Lilita Gcwabe" />
      <Input label="Role / Title *" value={f.role} onChange={e=>set("role",e.target.value)} placeholder="Founder & CEO" />
      <Textarea label="Bio *" span={2} value={f.bio} onChange={e=>set("bio",e.target.value)} rows={4} placeholder="Short biography…" />
      <Input label="Email (optional)" value={f.email} onChange={e=>set("email",e.target.value)} type="email" placeholder="name@thehealthplug.co.za" />
      <Input label="LinkedIn URL (optional)" value={f.linkedin_url} onChange={e=>set("linkedin_url",e.target.value)} placeholder="https://linkedin.com/in/…" />
      <div>
        <label style={S.label}>Profile Photo</label>
        <input type="file" accept="image/*" onChange={e=>setPhoto(e.target.files[0])} style={{ ...S.input, padding:"0.5rem", cursor:"pointer" }} />
        {editing?.photo_url && !photo && <img src={editing.photo_url} alt="current" style={{ width:56, height:56, objectFit:"cover", borderRadius:"50%", marginTop:"0.5rem" }} />}
      </div>
      <Input label="Display Order" value={f.sort_order} onChange={e=>set("sort_order",e.target.value)} type="number" min="0" />
      <div style={{ gridColumn:"1/-1" }}>
        <Toggle label="Published (visible on site)" checked={!!f.published} onChange={()=>set("published",!f.published)} />
      </div>
      {err && <p style={{ ...S.error, gridColumn:"1/-1" }}>{err}</p>}
      <FormActions onSave={()=>onSave(submit)} onCancel={onCancel} saving={saving} />
    </div>
  );
}

function TeamSection() {
  const { items, loading, showForm, editing, saving, err, openAdd, openEdit, close, save, remove } = useSection(()=>api.adminAll("team"));
  return (
    <>
      <SectionHeader title="Team Members" onAdd={openAdd} addLabel="+ Add Team Member" />
      {showForm && (
        <FormWrap title={editing ? "Edit Team Member" : "New Team Member"} onClose={close}>
          <TeamForm editing={editing} onSave={save} onCancel={close} saving={saving} err={err} />
        </FormWrap>
      )}
      {loading ? <p style={{ color:"rgba(255,255,255,0.4)" }}>Loading…</p> : items.map(m => (
        <div key={m.id} style={S.row}>
          <div style={{ width:48, height:48, borderRadius:"50%", background:"linear-gradient(135deg,#8A1613,#95BB83)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.2rem", flexShrink:0, overflow:"hidden" }}>
            {m.photo_url ? <img src={m.photo_url} alt={m.name} style={{ width:"100%", height:"100%", objectFit:"cover" }} /> : "👤"}
          </div>
          <div style={{ flex:1 }}>
            <p style={{ fontWeight:600, fontSize:"0.92rem", color:"#fff", margin:0 }}>{m.name}</p>
            <p style={{ fontSize:"0.75rem", color:"rgba(255,255,255,0.35)", margin:0 }}>{m.role}</p>
          </div>
          <div style={{ display:"flex", gap:"0.5rem" }}>
            {!m.published && <span style={S.badge("rgba(255,255,255,0.4)")}>Hidden</span>}
            <BtnEdit onClick={()=>openEdit(m)} />
            <BtnDanger onClick={()=>remove(m.id, api.deleteTeamMember)}>Delete</BtnDanger>
          </div>
        </div>
      ))}
    </>
  );
}

// ─────────────────────────────────────────────
// TESTIMONIALS
// ─────────────────────────────────────────────
function TestimonialForm({ editing, onSave, onCancel, saving, err }) {
  const empty = { quote:"", author_name:"", author_title:"", organisation:"", sort_order:0, featured:false, published:true };
  const [f, setF] = useState(editing || empty);
  const [logo,  setLogo]  = useState(null);
  const [photo, setPhoto] = useState(null);
  const set = (k,v) => setF(p=>({...p,[k]:v}));

  const submit = () => {
    const fd = new FormData();
    Object.entries(f).forEach(([k,v])=>fd.append(k,v));
    if (logo)  fd.append("logo",  logo);
    if (photo) fd.append("photo", photo);
    return editing ? api.updateTestimonial(editing.id, fd) : api.createTestimonial(fd);
  };

  return (
    <div style={S.grid2}>
      <Textarea label="Quote *" span={2} value={f.quote} onChange={e=>set("quote",e.target.value)} rows={4} placeholder="What they said…" />
      <Input label="Author Name *" value={f.author_name} onChange={e=>set("author_name",e.target.value)} placeholder="Jane Dube" />
      <Input label="Author Title" value={f.author_title} onChange={e=>set("author_title",e.target.value)} placeholder="Campaign Manager" />
      <Input label="Organisation" value={f.organisation} onChange={e=>set("organisation",e.target.value)} placeholder="TB Proof" />
      <Input label="Display Order" value={f.sort_order} onChange={e=>set("sort_order",e.target.value)} type="number" min="0" />
      <div>
        <label style={S.label}>Organisation Logo</label>
        <input type="file" accept="image/*" onChange={e=>setLogo(e.target.files[0])} style={{ ...S.input, padding:"0.5rem", cursor:"pointer" }} />
      </div>
      <div>
        <label style={S.label}>Author Photo</label>
        <input type="file" accept="image/*" onChange={e=>setPhoto(e.target.files[0])} style={{ ...S.input, padding:"0.5rem", cursor:"pointer" }} />
      </div>
      <div style={{ gridColumn:"1/-1", display:"flex", gap:"2rem" }}>
        <Toggle label="Featured"          checked={!!f.featured}  onChange={()=>set("featured", !f.featured)}  />
        <Toggle label="Published"         checked={!!f.published} onChange={()=>set("published",!f.published)} />
      </div>
      {err && <p style={{ ...S.error, gridColumn:"1/-1" }}>{err}</p>}
      <FormActions onSave={()=>onSave(submit)} onCancel={onCancel} saving={saving} />
    </div>
  );
}

function TestimonialsSection() {
  const { items, loading, showForm, editing, saving, err, openAdd, openEdit, close, save, remove } = useSection(()=>api.adminAll("testimonials"));
  return (
    <>
      <SectionHeader title="Testimonials" onAdd={openAdd} addLabel="+ Add Testimonial" />
      {showForm && (
        <FormWrap title={editing ? "Edit Testimonial" : "New Testimonial"} onClose={close}>
          <TestimonialForm editing={editing} onSave={save} onCancel={close} saving={saving} err={err} />
        </FormWrap>
      )}
      {loading ? <p style={{ color:"rgba(255,255,255,0.4)" }}>Loading…</p> : items.map(t => (
        <div key={t.id} style={S.row}>
          <div style={{ fontSize:"1.5rem", flexShrink:0 }}>💬</div>
          <div style={{ flex:1 }}>
            <p style={{ fontWeight:600, fontSize:"0.88rem", color:"rgba(255,255,255,0.85)", margin:0, fontStyle:"italic" }}>"{t.quote.substring(0,80)}…"</p>
            <p style={{ fontSize:"0.75rem", color:"rgba(255,255,255,0.35)", margin:0 }}>{t.author_name}{t.organisation ? ` · ${t.organisation}` : ""}</p>
          </div>
          <div style={{ display:"flex", gap:"0.5rem" }}>
            {t.featured  && <span style={S.badge("#95BB83")}>Featured</span>}
            {!t.published && <span style={S.badge("rgba(255,255,255,0.4)")}>Draft</span>}
            <BtnEdit onClick={()=>openEdit(t)} />
            <BtnDanger onClick={()=>remove(t.id, api.deleteTestimonial)}>Delete</BtnDanger>
          </div>
        </div>
      ))}
    </>
  );
}

// ─────────────────────────────────────────────
// CLIENTS & PARTNERS
// ─────────────────────────────────────────────
function ClientForm({ editing, onSave, onCancel, saving, err }) {
  const empty = { name:"", type:"client", website_url:"", description:"", sort_order:0, featured:false, published:true };
  const [f, setF] = useState(editing || empty);
  const [logo, setLogo] = useState(null);
  const set = (k,v) => setF(p=>({...p,[k]:v}));

  const submit = () => {
    const fd = new FormData();
    Object.entries(f).forEach(([k,v])=>fd.append(k,v));
    if (logo) fd.append("logo", logo);
    return editing ? api.updateClient(editing.id, fd) : api.createClient(fd);
  };

  return (
    <div style={S.grid2}>
      <Input label="Name *" value={f.name} onChange={e=>set("name",e.target.value)} placeholder="TB Proof" />
      <Select label="Type" value={f.type} onChange={e=>set("type",e.target.value)}
        options={[["client","Client"],["partner","Partner"],["collaborator","Collaborator"],["supporter","Supporter"]]} />
      <Input label="Website URL" value={f.website_url} onChange={e=>set("website_url",e.target.value)} placeholder="https://…" />
      <Input label="Display Order" value={f.sort_order} onChange={e=>set("sort_order",e.target.value)} type="number" min="0" />
      <Textarea label="Description" span={2} value={f.description} onChange={e=>set("description",e.target.value)} rows={3} placeholder="Short description…" />
      <div>
        <label style={S.label}>Logo Image</label>
        <input type="file" accept="image/*" onChange={e=>setLogo(e.target.files[0])} style={{ ...S.input, padding:"0.5rem", cursor:"pointer" }} />
        {editing?.logo_url && !logo && <img src={editing.logo_url} alt="logo" style={{ height:40, marginTop:"0.5rem", objectFit:"contain" }} />}
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:"0.75rem" }}>
        <Toggle label="Featured" checked={!!f.featured} onChange={()=>set("featured",!f.featured)} />
        <Toggle label="Published" checked={!!f.published} onChange={()=>set("published",!f.published)} />
      </div>
      {err && <p style={{ ...S.error, gridColumn:"1/-1" }}>{err}</p>}
      <FormActions onSave={()=>onSave(submit)} onCancel={onCancel} saving={saving} />
    </div>
  );
}

const typeColor = { client:"#8A1613", partner:"#007965", collaborator:"#534AB7", supporter:"#B07D1A" };

function ClientsSection() {
  const { items, loading, showForm, editing, saving, err, openAdd, openEdit, close, save, remove } = useSection(()=>api.adminAll("clients"));
  return (
    <>
      <SectionHeader title="Clients & Partners" onAdd={openAdd} addLabel="+ Add Client / Partner" />
      {showForm && (
        <FormWrap title={editing ? "Edit Entry" : "New Client / Partner"} onClose={close}>
          <ClientForm editing={editing} onSave={save} onCancel={close} saving={saving} err={err} />
        </FormWrap>
      )}
      {loading ? <p style={{ color:"rgba(255,255,255,0.4)" }}>Loading…</p> : items.map(c => (
        <div key={c.id} style={S.row}>
          <div style={{ width:44, height:44, borderRadius:10, background:"rgba(255,255,255,0.06)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, overflow:"hidden" }}>
            {c.logo_url ? <img src={c.logo_url} alt={c.name} style={{ width:"100%", height:"100%", objectFit:"contain" }} /> : "🏢"}
          </div>
          <div style={{ flex:1 }}>
            <p style={{ fontWeight:600, fontSize:"0.92rem", color:"#fff", margin:0 }}>{c.name}</p>
            <p style={{ fontSize:"0.75rem", color:"rgba(255,255,255,0.35)", margin:0 }}>{c.description?.substring(0,60) || "No description"}</p>
          </div>
          <div style={{ display:"flex", gap:"0.5rem", alignItems:"center" }}>
            <span style={S.badge(typeColor[c.type] || "#888")}>{c.type}</span>
            {c.featured && <span style={S.badge("#95BB83")}>Featured</span>}
            <BtnEdit onClick={()=>openEdit(c)} />
            <BtnDanger onClick={()=>remove(c.id, api.deleteClient)}>Delete</BtnDanger>
          </div>
        </div>
      ))}
    </>
  );
}

// ── Service Form ──────────────────────────────────────────────
function ServiceForm({ editing, onSave, onCancel, saving, err }) {
  const empty = {
    icon: "🎯", title: "", description: "", detail: "",
    sort_order: 0, published: true,
  };
  const [f, setF] = useState(editing || empty);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  // Common emoji picks to make it easy
  const iconPicks = ["🎯","🎬","📰","🎙","🌍","🗣","📊","🏥","💡","🤝","📡","📱","🖊","📋","🔬","💊"];

  const submit = () => {
    const data = { ...f, published: f.published ? 1 : 0 };
    return editing
      ? api.updateService(editing.id, data)
      : api.createService(data);
  };

  return (
    <div style={S.grid2}>
      {/* Icon picker */}
      <div style={{ gridColumn: "1/-1" }}>
        <label style={S.label}>Icon (pick one or type any emoji)</label>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.5rem" }}>
          {iconPicks.map(ic => (
            <button
              key={ic}
              type="button"
              onClick={() => set("icon", ic)}
              style={{
                width: 42, height: 42, borderRadius: 10, fontSize: "1.3rem",
                border: f.icon === ic
                  ? "2px solid #8A1613"
                  : "1.5px solid rgba(255,255,255,0.12)",
                background: f.icon === ic ? "rgba(138,22,19,0.2)" : "rgba(255,255,255,0.06)",
                cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.15s",
              }}
            >
              {ic}
            </button>
          ))}
        </div>
        <input
          value={f.icon}
          onChange={e => set("icon", e.target.value)}
          placeholder="Or type any emoji…"
          style={{ ...S.input, maxWidth: 120 }}
          maxLength={4}
        />
      </div>

      <Input label="Title *"       value={f.title}       onChange={e => set("title",       e.target.value)} placeholder="e.g. Health Campaign Strategy" />
      <Input label="Sort Order"    value={f.sort_order}  onChange={e => set("sort_order",  e.target.value)} type="number" min="0" />
      <Textarea label="Short Description * (shown on cards)"
        span={2} value={f.description}
        onChange={e => set("description", e.target.value)}
        rows={3}
        placeholder="One or two sentences shown on the services card…"
      />
      <Textarea label="Detail paragraph (optional — shown below description)"
        span={2} value={f.detail}
        onChange={e => set("detail", e.target.value)}
        rows={3}
        placeholder="A bit more context shown on the full Services page…"
      />
      <div style={{ gridColumn: "1/-1" }}>
        <Toggle
          label="Published (visible on site)"
          checked={!!f.published}
          onChange={() => set("published", !f.published)}
        />
      </div>

      {err && <p style={{ ...S.error, gridColumn: "1/-1" }}>{err}</p>}
      <FormActions onSave={() => onSave(submit)} onCancel={onCancel} saving={saving} />
    </div>
  );
}

// ── Services Section ──────────────────────────────────────────
function ServicesSection() {
  const {
    items, loading, showForm, editing, saving, err,
    openAdd, openEdit, close, save, remove,
  } = useSection(() => api.getServices());

  return (
    <>
      <SectionHeader title="Services" onAdd={openAdd} addLabel="+ Add Service" />
      <p style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.85rem", marginBottom: "1.5rem" }}>
        Changes here update the Services page instantly — no code changes needed.
      </p>

      {showForm && (
        <FormWrap title={editing ? "Edit Service" : "New Service"} onClose={close}>
          <ServiceForm editing={editing} onSave={save} onCancel={close} saving={saving} err={err} />
        </FormWrap>
      )}

      {loading
        ? <p style={{ color: "rgba(255,255,255,0.4)" }}>Loading…</p>
        : items.map((s, i) => (
          <div key={s.id} style={S.row}>
            {/* Icon bubble */}
            <div style={{
              width: 44, height: 44, borderRadius: 12, flexShrink: 0,
              background: i % 2 === 0 ? "rgba(138,22,19,0.3)" : "rgba(0,121,101,0.3)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "1.3rem",
            }}>
              {s.icon}
            </div>

            <div style={{ flex: 1 }}>
              <p style={{ fontWeight: 600, fontSize: "0.92rem", color: "#fff", margin: 0 }}>
                {s.title}
              </p>
              <p style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.35)", margin: 0 }}>
                {s.description.slice(0, 70)}{s.description.length > 70 ? "…" : ""}
              </p>
            </div>

            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <span style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.25)", fontFamily: "var(--font)" }}>
                #{s.sort_order}
              </span>
              {!s.published && (
                <span style={S.badge("rgba(255,255,255,0.4)")}>Draft</span>
              )}
              <BtnEdit onClick={() => openEdit(s)} />
              <BtnDanger onClick={() => remove(s.id, api.deleteService)}>Delete</BtnDanger>
            </div>
          </div>
        ))
      }
    </>
  );
}

// ─────────────────────────────────────────────
// ARTICLES / HEALTH ARCHIVE
// ─────────────────────────────────────────────

function ArticleForm({ editing, onSave, onCancel, saving, err }) {
  const empty = {
    title:"", excerpt:"", body:"", category:"",
    author:"The Health Plug",
    publish_date: new Date().toISOString().slice(0,10),
    featured: false, published: true,
    notify_subscribers: false,   // ← NEW
  };
  const [f, setF] = useState(editing || empty);
  const [cover, setCover] = useState(null);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const submit = () => {
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    // Ensure booleans are sent as "1"/"0"
    fd.set("featured",           f.featured           ? "1" : "0");
    fd.set("published",          f.published          ? "1" : "0");
    fd.set("notify_subscribers", f.notify_subscribers ? "1" : "0");
    if (cover) fd.append("cover", cover);
    return editing ? api.updateArticle(editing.id, fd) : api.createArticle(fd);
  };

  const cats = [
    "Sexual Health","Mental Health","Tuberculosis","HIV/AIDS","Diabetes",
    "Maternal Health","Environmental Health","Health Rights","Community Care","Podcast Notes",
  ];

  return (
    <div style={S.grid2}>
      <Input label="Title *" span={2} value={f.title} onChange={e=>set("title",e.target.value)} placeholder="Article title" />
      <Select label="Category *" value={f.category} onChange={e=>set("category",e.target.value)}
        options={[["","Select category…"],...cats.map(c=>[c,c])]} />
      <Input label="Author" value={f.author} onChange={e=>set("author",e.target.value)} placeholder="The Health Plug" />
      <Textarea label="Excerpt *" span={2} value={f.excerpt} onChange={e=>set("excerpt",e.target.value)} rows={2} placeholder="Short summary shown in article cards…" />
      <Textarea label="Body (HTML or plain text) *" span={2} value={f.body} onChange={e=>set("body",e.target.value)} rows={10} placeholder="<p>Full article content…</p>" />
      <div>
        <label style={S.label}>Cover Image</label>
        <input type="file" accept="image/*" onChange={e=>setCover(e.target.files[0])} style={{ ...S.input, padding:"0.5rem", cursor:"pointer" }} />
        {editing?.cover_url && !cover && (
          <img src={editing.cover_url} alt="cover" style={{ width:"100%", height:80, objectFit:"cover", borderRadius:8, marginTop:"0.5rem" }} />
        )}
      </div>
      <Input label="Publish Date *" value={f.publish_date} onChange={e=>set("publish_date",e.target.value)} type="date" />

      {/* Toggles */}
      <div style={{ gridColumn:"1/-1", display:"flex", flexDirection:"column", gap:"0.85rem" }}>
        <Toggle label="Featured" checked={!!f.featured} onChange={()=>set("featured",!f.featured)} />
        <Toggle label="Published" checked={!!f.published} onChange={()=>set("published",!f.published)} />
        {!editing && (
          <Toggle
            label="📧 Notify newsletter subscribers when published"
            checked={!!f.notify_subscribers}
            onChange={()=>set("notify_subscribers",!f.notify_subscribers)}
          />
        )}
      </div>

      {err && <p style={{ ...S.error, gridColumn:"1/-1" }}>{err}</p>}
      <FormActions onSave={()=>onSave(submit)} onCancel={onCancel} saving={saving} saveLabel={editing?"Update Article":"Publish Article"} />
    </div>
  );
}

function ArticlesSection() {
  const { items, loading, showForm, editing, saving, err, openAdd, openEdit, close, save, remove } = useSection(()=>api.adminAll("articles"));
  return (
    <>
      <SectionHeader title="Health Archive (Blog)" onAdd={openAdd} addLabel="+ Write Article" />
      {showForm && (
        <FormWrap title={editing ? "Edit Article" : "New Article"} onClose={close}>
          <ArticleForm editing={editing} onSave={save} onCancel={close} saving={saving} err={err} />
        </FormWrap>
      )}
      {loading ? <p style={{ color:"rgba(255,255,255,0.4)" }}>Loading…</p> : items.map(a => (
        <div key={a.id} style={S.row}>
          <div style={{ width:44, height:44, borderRadius:10, background:"rgba(149,187,131,0.15)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.2rem", flexShrink:0 }}>📝</div>
          <div style={{ flex:1 }}>
            <p style={{ fontWeight:600, fontSize:"0.92rem", color:"#fff", margin:0 }}>{a.title}</p>
            <p style={{ fontSize:"0.75rem", color:"rgba(255,255,255,0.35)", margin:0 }}>{a.category} · {a.author} · {new Date(a.publish_date).toLocaleDateString("en-ZA")}</p>
          </div>
          <div style={{ display:"flex", gap:"0.5rem", alignItems:"center" }}>
            {a.featured && <span style={S.badge("#95BB83")}>Featured</span>}
            {!a.published && <span style={S.badge("rgba(255,255,255,0.4)")}>Draft</span>}
            <BtnEdit onClick={()=>openEdit(a)} />
            <BtnDanger onClick={()=>remove(a.id, api.deleteArticle)}>Delete</BtnDanger>
          </div>
        </div>
      ))}
    </>
  );
}

// ─────────────────────────────────────────────
// CONTACTS
// ─────────────────────────────────────────────
function ContactsSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.adminAll("contacts").then(setItems).finally(()=>setLoading(false)); }, []);

  const updateStatus = async (id, status) => {
    await api.updateContactStatus(id, status);
    setItems(prev => prev.map(c => c.id===id ? {...c, status} : c));
  };

  const statusColor = { new:"#8A1613", read:"#B07D1A", replied:"#007965" };

  return (
    <>
      <SectionHeader title="Contact Submissions" />
      {loading ? <p style={{ color:"rgba(255,255,255,0.4)" }}>Loading…</p> : items.map(c => (
        <div key={c.id} style={{ ...S.card, borderLeft:`3px solid ${statusColor[c.status]||"#888"}` }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"0.75rem" }}>
            <div>
              <p style={{ fontWeight:700, fontSize:"0.95rem", color:"#fff", margin:0 }}>{c.name}{c.company ? ` — ${c.company}` : ""}</p>
              <p style={{ fontSize:"0.78rem", color:"rgba(255,255,255,0.4)", margin:"0.2rem 0 0" }}>{c.email}{c.phone ? ` · ${c.phone}` : ""} · {new Date(c.created_at).toLocaleDateString("en-ZA")}</p>
            </div>
            <div style={{ display:"flex", gap:"0.5rem", alignItems:"center" }}>
              <span style={S.badge(statusColor[c.status]||"#888")}>{c.status}</span>
              {c.status==="new"  && <BtnEdit onClick={()=>updateStatus(c.id,"read")}>Mark Read</BtnEdit>}
              {c.status==="read" && <BtnEdit onClick={()=>updateStatus(c.id,"replied")}>Mark Replied</BtnEdit>}
            </div>
          </div>
          {c.campaign_type && <p style={{ fontSize:"0.8rem", color:"#8A1613", margin:"0 0 0.4rem", fontWeight:600 }}>Enquiry: {c.campaign_type}</p>}
          {c.message && <p style={{ fontSize:"0.85rem", color:"rgba(255,255,255,0.5)", lineHeight:1.65, margin:0 }}>{c.message}</p>}
          <a href={`mailto:${c.email}?subject=Re: ${c.campaign_type || "Your Enquiry"}`}
            style={{ display:"inline-block", marginTop:"0.75rem", fontSize:"0.78rem", color:"#95BB83" }}>
            Reply via email →
          </a>
        </div>
      ))}
    </>
  );
}

// ─────────────────────────────────────────────
// NEWSLETTER
// ─────────────────────────────────────────────
function NewsletterSection() {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab,     setTab]     = useState("subscribers");
  const [blast, setBlast] = useState({
    subject:"", headingLine1:"", headingLine2:"",
    bodyHtml:"", ctaLabel:"", ctaUrl:"", preheader:"", type:"update",
  });
  const [sending,  setSending]  = useState(false);
  const [blastMsg, setBlastMsg] = useState("");
  const [blastErr, setBlastErr] = useState("");
  const setB = (k, v) => setBlast(p => ({ ...p, [k]: v }));

  useEffect(() => {
    api.adminAll("newsletter").then(setItems).finally(() => setLoading(false));
  }, []);

  const active   = items.filter(s => s.active);
  const inactive = items.filter(s => !s.active);

  const sendBlast = async () => {
    if (!blast.subject || !blast.headingLine1 || !blast.bodyHtml) {
      setBlastErr("Subject, heading and body are required."); return;
    }
    setSending(true); setBlastErr(""); setBlastMsg("");
    try {
      const res = await api.sendNewsletterBlast(blast);
      setBlastMsg(`✅ ${res.message}`);
      setBlast({ subject:"", headingLine1:"", headingLine2:"", bodyHtml:"", ctaLabel:"", ctaUrl:"", preheader:"", type:"update" });
    } catch (e) { setBlastErr(e.message); }
    finally { setSending(false); }
  };

  const tabBtn = (key, label) => (
    <button onClick={() => setTab(key)} style={{
      padding:"0.5rem 1.25rem", border:"none", borderRadius:100,
      background: tab===key ? "#8A1613" : "rgba(255,255,255,0.07)",
      color: tab===key ? "#fff" : "rgba(255,255,255,0.5)",
      fontFamily:"var(--font)", fontWeight:600, fontSize:"0.82rem", cursor:"pointer",
      transition:"all 0.2s",
    }}>{label}</button>
  );

  return (
    <>
      <SectionHeader title="Newsletter" />

      {/* Stats */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:"1rem", marginBottom:"1.5rem" }}>
        {[
          ["Total",    items.length,    "#fff"],
          ["Active",   active.length,   "#95BB83"],
          ["Inactive", inactive.length, "rgba(255,255,255,0.35)"],
        ].map(([l,v,c]) => (
          <div key={l} style={{ ...S.card, textAlign:"center", marginBottom:0 }}>
            <p style={{ fontFamily:"var(--font)", fontSize:"2rem", fontWeight:800, color:c, margin:0 }}>{v}</p>
            <p style={{ fontSize:"0.78rem", color:"rgba(255,255,255,0.35)", margin:"0.25rem 0 0" }}>{l}</p>
          </div>
        ))}
      </div>

      {/* Tab switcher */}
      <div style={{ display:"flex", gap:"0.5rem", marginBottom:"1.5rem" }}>
        {tabBtn("subscribers", `Subscribers (${items.length})`)}
        {tabBtn("blast", "Send Newsletter")}
      </div>

      {/* ── Subscribers table ── */}
      {tab === "subscribers" && (
        <div style={{ ...S.card, padding:0, overflow:"hidden" }}>
          {loading ? (
            <p style={{ color:"rgba(255,255,255,0.4)", padding:"1.5rem" }}>Loading…</p>
          ) : items.length === 0 ? (
            <p style={{ color:"rgba(255,255,255,0.4)", padding:"1.5rem" }}>No subscribers yet.</p>
          ) : (
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", minWidth:480 }}>
                <thead>
                  <tr style={{ borderBottom:"1px solid rgba(255,255,255,0.08)" }}>
                    {["Name","Email","Status","Joined"].map(h => (
                      <th key={h} style={{
                        textAlign:"left", padding:"0.85rem 1.25rem",
                        fontSize:"0.7rem", color:"rgba(255,255,255,0.35)",
                        textTransform:"uppercase", letterSpacing:"0.08em", whiteSpace:"nowrap",
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map(s => (
                    <tr key={s.id}
                      style={{ borderBottom:"1px solid rgba(255,255,255,0.05)", transition:"background 0.15s" }}
                      onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.03)"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                    >
                      <td style={{ padding:"0.75rem 1.25rem", fontSize:"0.88rem", color:"rgba(255,255,255,0.7)" }}>
                        {s.name || <span style={{ color:"rgba(255,255,255,0.25)", fontStyle:"italic" }}>—</span>}
                      </td>
                      <td style={{ padding:"0.75rem 1.25rem", fontSize:"0.88rem", color:"#fff" }}>{s.email}</td>
                      <td style={{ padding:"0.75rem 1.25rem" }}>
                        <span style={S.badge(s.active ? "#95BB83" : "rgba(255,255,255,0.3)")}>
                          {s.active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td style={{ padding:"0.75rem 1.25rem", fontSize:"0.78rem", color:"rgba(255,255,255,0.35)", whiteSpace:"nowrap" }}>
                        {new Date(s.created_at).toLocaleDateString("en-ZA",{year:"numeric",month:"short",day:"numeric"})}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Send blast tab ── */}
      {tab === "blast" && (
        <div style={S.card}>
          <p style={{ color:"rgba(255,255,255,0.4)", fontSize:"0.85rem", marginBottom:"1.5rem" }}>
            Send a custom newsletter to all{" "}
            <strong style={{ color:"#95BB83" }}>{active.length} active</strong> subscribers.
          </p>
          <div style={S.grid2}>
            <Input label="Email Subject *"        span={2} value={blast.subject}      onChange={e=>setB("subject",      e.target.value)} placeholder="e.g. New episode just dropped 🎙" />
            <Input label="Preheader text"          span={2} value={blast.preheader}    onChange={e=>setB("preheader",    e.target.value)} placeholder="Short preview text shown in inbox" />
            <Input label="Main Heading *"                   value={blast.headingLine1} onChange={e=>setB("headingLine1", e.target.value)} placeholder="e.g. Season 2 is here!" />
            <Input label="Sub-heading (optional)"           value={blast.headingLine2} onChange={e=>setB("headingLine2", e.target.value)} placeholder="e.g. Mental Health · Episode 7" />
            <Textarea label="Body (HTML or plain text) *" span={2}
              value={blast.bodyHtml} onChange={e=>setB("bodyHtml", e.target.value)}
              rows={5} placeholder="<p>Your message here…</p>" />
            <Input label="CTA Button label" value={blast.ctaLabel} onChange={e=>setB("ctaLabel", e.target.value)} placeholder="e.g. Listen Now" />
            <Input label="CTA Button URL"   value={blast.ctaUrl}   onChange={e=>setB("ctaUrl",   e.target.value)} placeholder="https://thehealthplug.co.za/podcast" />
            <Select label="Email type" value={blast.type} onChange={e=>setB("type", e.target.value)}
              options={[["update","General Update"],["episode","New Episode"],["article","New Article"]]} />

            {blastErr && <p style={{ ...S.error, gridColumn:"1/-1" }}>{blastErr}</p>}
            {blastMsg && <p style={{ gridColumn:"1/-1", color:"#95BB83", fontSize:"0.88rem" }}>{blastMsg}</p>}

            <div style={{ gridColumn:"1/-1", display:"flex", gap:"0.75rem", alignItems:"center" }}>
              <BtnPrimary onClick={sendBlast} disabled={sending || active.length === 0}>
                {sending ? "Sending…" : `Send to ${active.length} subscribers`}
              </BtnPrimary>
              {active.length === 0 && (
                <span style={{ fontSize:"0.82rem", color:"rgba(255,255,255,0.35)" }}>No active subscribers yet.</span>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────
// SETTINGS
// ─────────────────────────────────────────────
function SettingsSection() {
  const [rows,    setRows]    = useState([]);
  const [values,  setValues]  = useState({});
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [err,     setErr]     = useState("");

  useEffect(()=>{
    api.getSettingsFull().then(r=>{ setRows(r); const v={}; r.forEach(x=>v[x.setting_key]=x.setting_value||""); setValues(v); });
  },[]);

  const set = (k,v) => setValues(p=>({...p,[k]:v}));

  const submit = async () => {
    setSaving(true); setSaved(false); setErr("");
    try { await api.updateSettings(values); setSaved(true); setTimeout(()=>setSaved(false),2500); }
    catch(e){ setErr(e.message); }
    finally{ setSaving(false); }
  };

  const groups = [
    { label:"General",  keys:["site_tagline","about_blurb"] },
    { label:"Contact",  keys:["contact_email","contact_phone","contact_location","calendly_url"] },
    { label:"Social Media", keys:["instagram_url","tiktok_url","facebook_url","linkedin_url","youtube_url"] },
    { label:"Homepage", keys:["hero_cta_primary","hero_cta_secondary","newsletter_heading"] },
  ];

  return (
    <>
      <SectionHeader title="Site Settings" />
      <p style={{ color:"rgba(255,255,255,0.4)", fontSize:"0.88rem", marginBottom:"1.5rem" }}>Changes here update the website instantly — no code required.</p>
      {groups.map(g=>(
        <div key={g.label} style={{ ...S.card, marginBottom:"1.25rem" }}>
          <h3 style={{ fontFamily:"var(--font)", fontWeight:700, fontSize:"0.85rem", color:"#95BB83", textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:"1.25rem" }}>{g.label}</h3>
          <div style={S.grid2}>
            {rows.filter(r=>g.keys.includes(r.setting_key)).map(r=>(
              <div key={r.setting_key} style={{ gridColumn: r.type==="textarea" ? "1/-1" : "auto" }}>
                <label style={S.label}>{r.label}</label>
                {r.type==="textarea"
                  ? <textarea value={values[r.setting_key]||""} onChange={e=>set(r.setting_key,e.target.value)} rows={3} style={{ ...S.input, resize:"vertical" }} />
                  : <input value={values[r.setting_key]||""} onChange={e=>set(r.setting_key,e.target.value)} type={r.type==="email"?"email":r.type==="url"?"url":"text"} style={S.input} />
                }
              </div>
            ))}
          </div>
        </div>
      ))}
      {err    && <p style={S.error}>{err}</p>}
      {saved  && <p style={{ color:"#95BB83", fontSize:"0.85rem", marginBottom:"0.75rem" }}>✅ Settings saved!</p>}
      <BtnPrimary onClick={submit} disabled={saving}>{saving?"Saving…":"Save All Settings"}</BtnPrimary>
    </>
  );
}

// ─────────────────────────────────────────────
// PASSWORD CHANGE
// ─────────────────────────────────────────────
function PasswordSection({ username }) {
  const [cur,  setCur]  = useState("");
  const [nw,   setNw]   = useState("");
  const [conf, setConf] = useState("");
  const [msg,  setMsg]  = useState("");
  const [err,  setErr]  = useState("");
  const [saving,setSaving]=useState(false);

  const submit = async () => {
    if (!cur||!nw) { setErr("All fields required"); return; }
    const pwErr = validatePassword(nw);
    if (pwErr) { setErr(pwErr); return; }
    if (nw!==conf) { setErr("New passwords do not match"); return; }
    setSaving(true); setErr(""); setMsg("");
    try { await api.changePassword(cur,nw); setMsg("Password updated!"); setCur(""); setNw(""); setConf(""); }
    catch(e){ setErr(e.message); }
    finally{ setSaving(false); }
  };

  return (
    <>
      <SectionHeader title="Account" />
      <div style={{ ...S.card, maxWidth:480 }}>
        <p style={{ fontSize:"0.85rem", color:"rgba(255,255,255,0.4)", marginBottom:"1.5rem" }}>Signed in as <strong style={{ color:"#fff" }}>{username}</strong></p>
        <h3 style={{ fontFamily:"var(--font)", fontWeight:700, fontSize:"1rem", color:"#fff", marginBottom:"1.25rem" }}>Change Password</h3>
        <div style={{ display:"flex", flexDirection:"column", gap:"1rem" }}>
          <PwInput label="Current Password" value={cur} onChange={e=>setCur(e.target.value)} />
          <PwInput label="New Password" value={nw} onChange={e=>setNw(e.target.value)} showReqs={true} />
          <PwInput label="Confirm New Password" value={conf} onChange={e=>setConf(e.target.value)} />
          {err && <p style={S.error}>{err}</p>}
          {msg && <p style={{ color:"#95BB83", fontSize:"0.85rem" }}>✅ {msg}</p>}
          <BtnPrimary onClick={submit} disabled={saving}>{saving?"Updating…":"Update Password"}</BtnPrimary>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────
// STATS OVERVIEW
// ─────────────────────────────────────────────
function Overview({ stats }) {
  const cards = [
    { label:"Campaigns",    val:stats?.campaigns,    icon:"🎯", color:"#8A1613" },
    { label:"Episodes",     val:stats?.episodes,     icon:"🎙", color:"#007965" },
    { label:"Team Members", val:stats?.team,         icon:"👥", color:"#534AB7" },
    { label:"Testimonials", val:stats?.testimonials, icon:"💬", color:"#B07D1A" },
    { label:"Clients",      val:stats?.clients,      icon:"🏢", color:"#8A1613" },
    { label:"Articles",     val:stats?.articles,     icon:"📝", color:"#007965" },
    { label:"Enquiries",    val:stats?.contacts,     icon:"📬", color:"#B07D1A" },
    { label:"Unread",       val:stats?.unread,       icon:"🔴", color:"#8A1613" },
    { label:"Subscribers",  val:stats?.subscribers,  icon:"📧", color:"#007965" },
  ];
  return (
    <>
      <h2 style={{ fontFamily:"var(--font)", fontWeight:800, fontSize:"1.4rem", color:"#fff", marginBottom:"1.5rem" }}>Dashboard Overview</h2>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:"1rem", marginBottom:"2rem" }}>
        {cards.map(c=>(
          <div key={c.label} style={{ background:"#1a1a1a", border:`1px solid ${c.color}33`, borderRadius:14, padding:"1.25rem" }}>
            <p style={{ fontSize:"1.75rem", margin:"0 0 0.25rem" }}>{c.icon}</p>
            <p style={{ fontFamily:"var(--font)", fontSize:"2rem", fontWeight:900, color:c.color, margin:0, lineHeight:1 }}>{c.val ?? "—"}</p>
            <p style={{ fontSize:"0.75rem", color:"rgba(255,255,255,0.35)", margin:"0.3rem 0 0" }}>{c.label}</p>
          </div>
        ))}
      </div>
      <div style={{ ...S.card }}>
        <p style={{ fontFamily:"var(--font)", fontWeight:700, fontSize:"0.9rem", color:"rgba(255,255,255,0.6)", marginBottom:"0.75rem" }}>Quick tips</p>
        {[
          "Use the sidebar to manage any section of the site.",
          "Toggle Published on/off to hide content without deleting it.",
          "Use Sort Order (lower = higher up) to control display order.",
          "Settings → update social links, contact details & site text without touching code.",
          "Campaigns marked Featured appear on the homepage automatically.",
        ].map((t,i)=>(
          <p key={i} style={{ fontSize:"0.85rem", color:"rgba(255,255,255,0.4)", marginBottom:"0.4rem", paddingLeft:"1rem", borderLeft:"2px solid rgba(138,22,19,0.4)" }}>
            {t}
          </p>
        ))}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────
// ROLE / PERMISSION HELPERS
// ─────────────────────────────────────────────
const ROLE_PERMISSIONS = {
  super_admin:   ["manage_users","manage_episodes","manage_articles","manage_campaigns","manage_team","manage_clients","manage_testimonials","manage_settings","manage_services","view_contacts","manage_newsletter"],
  admin:         ["manage_episodes","manage_articles","manage_campaigns","manage_team","manage_clients","manage_testimonials","manage_settings","manage_services","view_contacts","manage_newsletter"],
  content_admin: ["manage_episodes","manage_articles"],
  custom:        [],
};

function canAccess(admin, permission) {
  if (!admin) return false;
  const role = admin.role || "admin";
  if (role === "super_admin") return true;
  if (role === "custom")      return (admin.permissions || []).includes(permission);
  return (ROLE_PERMISSIONS[role] || []).includes(permission);
}

// ─────────────────────────────────────────────
// USER MANAGEMENT SECTION
// ─────────────────────────────────────────────
const ROLE_LABELS = { super_admin:"Super Admin", admin:"Admin", content_admin:"Content Admin", custom:"Custom" };
const ALL_PERMISSIONS_LIST = ["manage_users","manage_episodes","manage_articles","manage_campaigns","manage_team","manage_clients","manage_testimonials","manage_settings","manage_services","view_contacts","manage_newsletter"];

function UsersSection() {
  const { items, loading, showForm, editing, saving, err, setErr, openAdd, openEdit, close, save, remove } = useSection(() => api.getUsers());

  const emptyUser = { username:"", password:"", role:"admin", permissions:[] };
  const [f, setF] = useState(emptyUser);
  const set = (k,v) => setF(p=>({...p,[k]:v}));

  useEffect(() => { if (editing) setF({ username: editing.username, password:"", role: editing.role, permissions: editing.permissions || [] }); else setF(emptyUser); }, [editing]);

  const togglePerm = (p) => setF(prev => ({ ...prev, permissions: prev.permissions.includes(p) ? prev.permissions.filter(x=>x!==p) : [...prev.permissions, p] }));

  const submit = () => {
    const payload = { username: f.username, role: f.role, permissions: f.permissions };
    if (f.password) {
      const pwErr = validatePassword(f.password);
      if (pwErr) throw new Error(pwErr);
      payload.password = f.password;
    } else if (!editing) {
      throw new Error("Password is required");
    }
    return editing ? api.updateUser(editing.id, payload) : api.createUser({ ...payload, password: f.password });
  };

  const roleColor = { super_admin:"#8A1613", admin:"#007965", content_admin:"#B07D1A", custom:"#534AB7" };

  return (
    <>
      <SectionHeader title="User Management" onAdd={openAdd} addLabel="+ Add User" />
      {showForm && (
        <FormWrap title={editing ? "Edit User" : "New User"} onClose={close}>
          <div style={S.grid2}>
            <Input label="Username *" value={f.username} onChange={e=>set("username",e.target.value)} placeholder="e.g. lilita" />
            <PwInput label={editing ? "New Password (leave blank to keep)" : "Password *"} value={f.password} onChange={e=>set("password",e.target.value)} placeholder="Min 8 chars, upper, lower, number" showReqs={true} />
            <div style={{ gridColumn:"1/-1" }}>
              <label style={S.label}>Role *</label>
              <select style={{ ...S.input, cursor:"pointer" }} value={f.role} onChange={e=>set("role",e.target.value)}>
                <option value="super_admin">Super Admin — full access including user management</option>
                <option value="admin">Admin — full access except user management</option>
                <option value="content_admin">Content Admin — episodes & articles only</option>
                <option value="custom">Custom — choose permissions below</option>
              </select>
            </div>
            {f.role === "custom" && (
              <div style={{ gridColumn:"1/-1" }}>
                <label style={S.label}>Custom Permissions</label>
                <div style={{ display:"flex", flexWrap:"wrap", gap:"0.5rem", marginTop:"0.3rem" }}>
                  {ALL_PERMISSIONS_LIST.map(p => (
                    <label key={p} style={{ display:"flex", alignItems:"center", gap:"0.4rem", background: f.permissions.includes(p) ? "rgba(138,22,19,0.2)" : "rgba(255,255,255,0.05)", border: `1px solid ${f.permissions.includes(p) ? "#8A1613" : "rgba(255,255,255,0.1)"}`, borderRadius:8, padding:"0.35rem 0.7rem", cursor:"pointer", fontSize:"0.78rem", color:"rgba(255,255,255,0.7)" }}>
                      <input type="checkbox" checked={f.permissions.includes(p)} onChange={()=>togglePerm(p)} style={{ accentColor:"#8A1613" }} />
                      {p.replace("manage_","").replace("view_","view ")}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {err && <p style={{ ...S.error, gridColumn:"1/-1" }}>{err}</p>}
            <FormActions onSave={()=>save(submit)} onCancel={close} saving={saving} />
          </div>
        </FormWrap>
      )}
      {loading ? <p style={{ color:"rgba(255,255,255,0.4)" }}>Loading…</p> : items.map(u => (
        <div key={u.id} style={S.row}>
          <div style={{ width:40, height:40, borderRadius:"50%", background:`${(roleColor[u.role]||"#555")}22`, border:`1.5px solid ${roleColor[u.role]||"#555"}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.1rem", flexShrink:0 }}>👤</div>
          <div style={{ flex:1 }}>
            <p style={{ fontWeight:600, fontSize:"0.92rem", color: u.is_active ? "#fff" : "rgba(255,255,255,0.3)", margin:0 }}>{u.username}</p>
            <p style={{ fontSize:"0.72rem", color:"rgba(255,255,255,0.35)", margin:0 }}>
              {ROLE_LABELS[u.role] || u.role}
              {u.last_login && ` · Last login: ${new Date(u.last_login).toLocaleDateString("en-ZA")}`}
            </p>
          </div>
          <div style={{ display:"flex", gap:"0.5rem", alignItems:"center" }}>
            <span style={S.badge(roleColor[u.role]||"#555")}>{ROLE_LABELS[u.role]||u.role}</span>
            {!u.is_active && <span style={S.badge("rgba(255,100,100,0.7)")}>Inactive</span>}
            <BtnEdit onClick={()=>openEdit(u)} />
            <button onClick={async()=>{ await api.toggleUser(u.id); window.location.reload(); }} style={{ background:"rgba(255,255,255,0.05)", color:"rgba(255,255,255,0.5)", border:"none", borderRadius:8, padding:"0.4rem 0.9rem", fontSize:"0.78rem", cursor:"pointer", fontWeight:600 }}>
              {u.is_active ? "Deactivate" : "Activate"}
            </button>
            <BtnDanger onClick={()=>remove(u.id, api.deleteUser)}>Delete</BtnDanger>
          </div>
        </div>
      ))}
    </>
  );
}

// ─────────────────────────────────────────────
// AUDIT LOG SECTION
// ─────────────────────────────────────────────
const ACTION_COLOR = { CREATE:"#95BB83", UPDATE:"#B07D1A", DELETE:"#f09595", LOGIN:"#534AB7" };

function AuditLogSection() {
  const [logs,    setLogs]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState({ resource:"", action:"", limit:50 });

  const load = () => {
    setLoading(true);
    api.getAuditLogs(Object.fromEntries(Object.entries(filter).filter(([,v])=>v)))
      .then(d => { setLogs(d.logs); setTotal(d.total); })
      .catch(()=>{})
      .finally(()=>setLoading(false));
  };
  useEffect(load, []);

  const RESOURCES = ["","campaigns","episodes","articles","team","clients","testimonials","settings","users","auth","newsletter"];
  const ACTIONS   = ["","CREATE","UPDATE","DELETE","LOGIN"];

  return (
    <>
      <SectionHeader title="Audit Log" />
      <div style={{ ...S.card, display:"flex", gap:"1rem", flexWrap:"wrap", alignItems:"flex-end" }}>
        <div style={{ flex:1, minWidth:140 }}>
          <label style={S.label}>Resource</label>
          <select style={{ ...S.input, cursor:"pointer" }} value={filter.resource} onChange={e=>setFilter(p=>({...p,resource:e.target.value}))}>
            {RESOURCES.map(r=><option key={r} value={r}>{r||"All resources"}</option>)}
          </select>
        </div>
        <div style={{ flex:1, minWidth:140 }}>
          <label style={S.label}>Action</label>
          <select style={{ ...S.input, cursor:"pointer" }} value={filter.action} onChange={e=>setFilter(p=>({...p,action:e.target.value}))}>
            {ACTIONS.map(a=><option key={a} value={a}>{a||"All actions"}</option>)}
          </select>
        </div>
        <BtnPrimary onClick={load}>Filter</BtnPrimary>
      </div>
      <p style={{ fontSize:"0.75rem", color:"rgba(255,255,255,0.3)", marginBottom:"0.75rem" }}>{total} total entries</p>
      {loading ? <p style={{ color:"rgba(255,255,255,0.4)" }}>Loading…</p> : logs.map(log => (
        <div key={log.id} style={{ ...S.row, padding:"0.75rem 1.25rem" }}>
          <span style={{ ...S.badge(ACTION_COLOR[log.action]||"#888"), flexShrink:0 }}>{log.action}</span>
          <div style={{ flex:1, minWidth:0 }}>
            <p style={{ fontWeight:600, fontSize:"0.88rem", color:"#fff", margin:0 }}>
              <span style={{ color:"rgba(255,255,255,0.5)" }}>{log.username}</span>
              {" → "}
              {log.resource}{log.resource_id ? ` #${log.resource_id}` : ""}
            </p>
            {log.details && (
              <p style={{ fontSize:"0.72rem", color:"rgba(255,255,255,0.35)", margin:"0.1rem 0 0", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                {typeof log.details === "string" ? log.details : JSON.stringify(log.details)}
              </p>
            )}
          </div>
          <div style={{ textAlign:"right", flexShrink:0 }}>
            <p style={{ fontSize:"0.72rem", color:"rgba(255,255,255,0.3)", margin:0 }}>
              {new Date(log.created_at).toLocaleString("en-ZA")}
            </p>
            {log.ip_address && <p style={{ fontSize:"0.68rem", color:"rgba(255,255,255,0.2)", margin:0 }}>{log.ip_address}</p>}
          </div>
        </div>
      ))}
    </>
  );
}

// ─────────────────────────────────────────────
// SIDEBAR NAV
// ─────────────────────────────────────────────
const NAV_ITEMS = [
  { key:"overview",      icon:"📊", label:"Overview",           permission: null },
  { key:"campaigns",     icon:"🎯", label:"Campaigns",          permission: "manage_campaigns" },
  { key:"episodes",      icon:"🎙", label:"Podcast Episodes",   permission: "manage_episodes" },
  { key:"articles",      icon:"📝", label:"Health Archive",     permission: "manage_articles" },
  { key:"team",          icon:"👥", label:"Team Members",       permission: "manage_team" },
  { key:"testimonials",  icon:"💬", label:"Testimonials",       permission: "manage_testimonials" },
  { key:"clients",       icon:"🏢", label:"Clients & Partners", permission: "manage_clients" },
  { key:"contacts",      icon:"📬", label:"Enquiries",          permission: "view_contacts" },
  { key:"newsletter",    icon:"📧", label:"Newsletter",         permission: "manage_newsletter" },
  { key:"settings",      icon:"⚙️",  label:"Site Settings",     permission: "manage_settings" },
  { key:"services",      icon:"🛠", label:"Services",           permission: "manage_services" },
  { key:"users",         icon:"👤", label:"User Management",    permission: "manage_users" },
  { key:"audit",         icon:"📋", label:"Audit Log",          minRole: ["super_admin","admin"] },
  { key:"account",       icon:"🔐", label:"Account",            permission: null },
];

// ─────────────────────────────────────────────
// MAIN DASHBOARD
// ─────────────────────────────────────────────
export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [tab,   setTab]   = useState("overview");
  const [stats, setStats] = useState(null);

  useEffect(()=>{
    if (!admin) { navigate("/admin"); return; }
    api.getStats().then(setStats).catch(()=>{});
  }, [admin]);

  if (!admin) return null;

  const visibleNav = NAV_ITEMS.filter(n => {
    if (n.minRole) return n.minRole.includes(admin.role);
    if (n.permission === null) return true;
    return canAccess(admin, n.permission);
  });

  const navItem = (key) => ({
    display:"flex", alignItems:"center", gap:"0.65rem",
    width:"100%", textAlign:"left", padding:"0.65rem 0.9rem",
    borderRadius:10, border:"none", cursor:"pointer",
    background: tab===key ? "rgba(138,22,19,0.2)" : "transparent",
    color: tab===key ? "#fff" : "rgba(255,255,255,0.45)",
    fontSize:"0.85rem", fontFamily:"var(--font)", fontWeight: tab===key ? 700 : 500,
    transition:"all 0.15s", marginBottom:"0.15rem",
  });

  const roleLabel = { super_admin:"Super Admin", admin:"Admin", content_admin:"Content Admin", custom:"Custom" };

  return (
    <div style={S.page}>
      {/* Sidebar */}
      <div style={S.sidebar}>
        {/* Logo */}
        <div style={{ display:"flex", alignItems:"center", gap:"0.5rem", marginBottom:"2rem", paddingLeft:"0.5rem" }}>
          <div style={{ width:34, height:34, borderRadius:"50%", background:"linear-gradient(135deg,#8A1613,#95BB83)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1rem" }}>🫀</div>
          <div>
            <p style={{ fontFamily:"var(--font)", fontWeight:800, fontSize:"0.88rem", color:"#fff", margin:0 }}>Health Plug</p>
            <p style={{ fontSize:"0.68rem", color:"rgba(255,255,255,0.35)", margin:0 }}>Admin Panel</p>
          </div>
        </div>

        {/* Nav items */}
        <div style={{ flex:1 }}>
          {visibleNav.map(n=>(
            <button key={n.key} style={navItem(n.key)} onClick={()=>setTab(n.key)}>
              <span style={{ fontSize:"1rem" }}>{n.icon}</span>
              <span>{n.label}</span>
              {n.key==="contacts" && stats?.unread > 0 && (
                <span style={{ marginLeft:"auto", background:"#8A1613", color:"#fff", borderRadius:100, padding:"0.05rem 0.5rem", fontSize:"0.65rem", fontWeight:700 }}>{stats.unread}</span>
              )}
            </button>
          ))}
        </div>

        {/* Footer */}
        <div style={{ borderTop:"1px solid rgba(255,255,255,0.07)", paddingTop:"1rem", marginTop:"1rem" }}>
          <p style={{ fontSize:"0.72rem", color:"rgba(255,255,255,0.25)", paddingLeft:"0.5rem", marginBottom:"0.15rem" }}>{admin.username}</p>
          <p style={{ fontSize:"0.65rem", color:"rgba(255,255,255,0.15)", paddingLeft:"0.5rem", marginBottom:"0.5rem" }}>{roleLabel[admin.role]||admin.role}</p>
          <button style={{ ...navItem("x"), color:"rgba(255,180,180,0.6)" }} onClick={()=>{ logout(); navigate("/"); }}>
            <span>🚪</span><span>Sign Out</span>
          </button>
        </div>
      </div>

      {/* Main */}
      <div style={S.main}>
        {tab==="overview"     && <Overview stats={stats} />}
        {tab==="campaigns"    && <CampaignsSection />}
        {tab==="episodes"     && <EpisodesSection />}
        {tab==="team"         && <TeamSection />}
        {tab==="testimonials" && <TestimonialsSection />}
        {tab==="clients"      && <ClientsSection />}
        {tab==="articles"     && <ArticlesSection />}
        {tab==="contacts"     && <ContactsSection />}
        {tab==="newsletter"   && <NewsletterSection />}
        {tab==="settings"     && <SettingsSection />}
        {tab==="services"     && <ServicesSection />}
        {tab==="users"        && <UsersSection />}
        {tab==="audit"        && <AuditLogSection />}
        {tab==="account"      && <PasswordSection username={admin.username} />}
      </div>
    </div>
  );
}
